import { useState } from "react";
import { useParams } from "react-router";

import { useRoles } from "@vega-api/extended/auth/roles/useRoles";
import { useGetPermissionsForUser } from "@vega-api/generated/auth-permission-v1/auth-permission-v1";
import { RoleContext } from "@vega-api/generated/types/roleContext";
import { type RoleNew } from "@vega-api/generated/types/roleNew";
import { SubjectType } from "@vega-api/generated/types/subjectType";
import { type UserPermissionEntry } from "@vega-api/generated/types/userPermissionEntry";
import { type UsersPermissionEntries } from "@vega-api/generated/types/usersPermissionEntries";
import { LinkTextButton } from "@vega-core/components/Buttons/LinkTextButton";
import { TabNavigation } from "@vega-core/components/Layout/TabNavigation";
import { useStatusPopup } from "@vega-core/hooks/useStatusPopup";
import { m } from "@vega-core/paraglide/messages";
import { buildTimeFlags } from "@vega-core/utils/buildTimeFlags";
import { isTruthy } from "@vega-core/utils/isTruthy";

import { usePermissionsHelp } from "../../../context/PermissionsHelpContext";
import { FundsList } from "../components/FundsList";

import { RemoveAccessModal } from "./modals/RemoveAccessModal";

enum Section {
  Offerings = "offerings",
  Investments = "investments",
}

export const FundsAccess = () => {
  const [isOpenRemoveAccessModal, setIsOpenRemoveAccessModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState<
    UserPermissionEntry | undefined
  >();
  const [pendingSubjectId, setPendingSubjectId] = useState<string | null>(null);

  const { setSuccess } = useStatusPopup();
  const { id: userId = "" } = useParams();
  const { openWithRoute } = usePermissionsHelp();

  const { deleteRole, setRoleBySubject, getRoleById } = useRoles();

  const {
    data: userPermissions = [],
    isLoading: isLoadingPermissions,
    refetch: refetchPermissions,
  } = useGetPermissionsForUser(userId, {
    query: {
      enabled: isTruthy(userId),
      select: (data: UsersPermissionEntries) => data.entries,
    },
  });

  const handleRoleChange = (selectedRole: RoleNew, subjectId: string) => {
    if (isTruthy(selectedRole.id)) {
      setPendingSubjectId(subjectId);
      const fullRole = getRoleById(selectedRole.id) ?? selectedRole;
      setRoleBySubject({
        role: fullRole,
        targetUserId: userId,
        subjectId,
        options: {
          onSuccess: () => {
            refetchPermissions().finally(() => setPendingSubjectId(null));
          },
          onError: () => {
            setPendingSubjectId(null);
          },
        },
      });
    }
  };

  const handleRemoveAccessOption = (subjectId: string) => {
    setSelectedItem(
      userPermissions.find((item) => item.subjectId === subjectId),
    );
    setIsOpenRemoveAccessModal(true);
  };

  const handleDeleteRole = () => {
    if (
      isTruthy(selectedItem) &&
      isTruthy(selectedItem.roleId) &&
      isTruthy(selectedItem.subjectId)
    ) {
      deleteRole(
        {
          userId,
          subjectId: selectedItem.subjectId,
          roleId: selectedItem.roleId,
        },
        {
          onSuccess: () => {
            refetchPermissions();
            setSuccess({
              title: m.userManagement_permissions_statusRevokeSuccess(),
            });
            setIsOpenRemoveAccessModal(false);
          },
        },
      );
    }
  };

  const tabs = (() => {
    const items = [];
    items.push({
      component: (
        <FundsList
          isLoading={isLoadingPermissions}
          onRemoveAccess={handleRemoveAccessOption}
          onRoleChange={handleRoleChange}
          pendingSubjectId={pendingSubjectId}
          permissions={userPermissions}
          roleContext={RoleContext.PRE_INVESTMENT}
          subjectType={SubjectType.OPPORTUNITY}
        />
      ),
      title: m.userManagement_permissions_offerings(),
      index: Section.Offerings,
    });

    if (buildTimeFlags.reporting_settings_investments) {
      items.push({
        component: (
          <FundsList
            isLoading={isLoadingPermissions}
            onRemoveAccess={handleRemoveAccessOption}
            onRoleChange={handleRoleChange}
            pendingSubjectId={pendingSubjectId}
            permissions={userPermissions}
            roleContext={RoleContext.POST_INVESTMENT}
            subjectType={SubjectType.INVESTMENT}
          />
        ),
        title: m.userManagement_permissions_investments(),
        index: Section.Investments,
      });
    }

    return items;
  })();

  return (
    <>
      <RemoveAccessModal
        fundName={selectedItem?.subjectName ?? ""}
        onClose={() => setIsOpenRemoveAccessModal(false)}
        onConfirm={handleDeleteRole}
        open={isOpenRemoveAccessModal}
      />
      <div className="flex flex-col gap-4">
        <h2 className="heading-4">{m.userManagement_permissions_title()}</h2>
        <div>
          <p className="body-base-regular text-text/secondary">
            {m.userManagement_permissions_description()}
          </p>
          <LinkTextButton
            onClick={() => openWithRoute()}
            text={m.userManagement_permissions_moreAboutRoles()}
          />
        </div>
        <div className="text-text/primary rounded-lg border border-border/primary overflow-hidden px-6 py-8">
          <TabNavigation
            innerGap={false}
            tabClassName="bg-background/none"
            tabs={tabs}
          />
        </div>
      </div>
    </>
  );
};
